import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Award, Sparkles, ScrollText } from "lucide-react";

const AWARD_TYPES = [
  "Best Delegate",
  "High Commendation",
  "Outstanding Diplomacy",
  "Honorable Mention",
  "Verbal Mention",
  "Participation",
];

export default function ChairAwardsPage() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [councils, setCouncils] = useState([]);
  const [awards, setAwards] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [aiRec, setAiRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openAward, setOpenAward] = useState(false);
  const [openCert, setOpenCert] = useState(false);
  const [form, setForm] = useState({
    council_id: "", delegate_id: "", delegate_name: "", country: "", award_type: "Best Delegate", rationale: "",
  });
  const [certForm, setCertForm] = useState({
    delegate_id: "", award_type: "Participation", council: "", country: "",
  });

  useEffect(() => {
    api.get("/events").then((r) => {
      setEvents(r.data);
      if (r.data[0]) setSelectedEvent(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    api.get(`/councils?event_id=${selectedEvent}`).then((r) => setCouncils(r.data));
    api.get(`/awards?event_id=${selectedEvent}`).then((r) => setAwards(r.data));
    api.get(`/registrations?event_id=${selectedEvent}`).then((r) => setRegistrations(r.data));
    api.get(`/sessions?event_id=${selectedEvent}`).then((r) => setSessions(r.data));
  }, [selectedEvent]);

  const recommend = async (sessionId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/awards/recommend", { session_id: sessionId });
      setAiRec(data);
      toast.success("AI recommendations ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const saveAward = async (e) => {
    e.preventDefault();
    try {
      const reg = registrations.find((r) => r.delegate_id === form.delegate_id);
      await api.post("/awards", {
        event_id: selectedEvent,
        ...form,
        delegate_name: reg?.delegate_name || form.delegate_name,
      });
      setOpenAward(false);
      api.get(`/awards?event_id=${selectedEvent}`).then((r) => setAwards(r.data));
      toast.success("Award recorded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const issueCert = async (e) => {
    e.preventDefault();
    try {
      const reg = registrations.find((r) => r.delegate_id === certForm.delegate_id);
      const country = reg?.allocated_country_id || "";
      await api.post("/certificates/issue", {
        event_id: selectedEvent,
        delegate_id: certForm.delegate_id,
        award_type: certForm.award_type,
        council: certForm.council,
        country: country,
      });
      setOpenCert(false);
      toast.success("Certificate issued");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Awards Room
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Honor the best, issue the proof.
            </h1>
          </div>
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-[280px]" data-testid="awards-event-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* AI rec panel */}
        <div className="mt-8 rounded-2xl bg-[#0A192F] text-white p-6 border border-[#D4AF37]/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                AI Awards Panel
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Get data-backed recommendations from delegate scores.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {sessions.map((s) => (
                <Button
                  key={s.id}
                  onClick={() => recommend(s.id)}
                  disabled={busy}
                  className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold text-xs uppercase tracking-[0.18em]"
                  data-testid={`recommend-${s.id}`}
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> {s.agenda.slice(0, 28)}…
                </Button>
              ))}
            </div>
          </div>
          {aiRec && (
            <div className="mt-5 grid md:grid-cols-2 gap-4 text-sm">
              {Object.entries(aiRec).map(([k, v]) => (
                <div key={k} className="p-4 rounded-md bg-white/5 border border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                    {k.replace(/_/g, " ")}
                  </p>
                  <p className="mt-2 text-slate-200">
                    {typeof v === "string" ? v : JSON.stringify(v)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center gap-3">
          <Dialog open={openAward} onOpenChange={setOpenAward}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="record-award-button">
                <Award className="h-4 w-4 mr-1" /> Record award
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record an award</DialogTitle></DialogHeader>
              <form onSubmit={saveAward} className="space-y-4" data-testid="award-form">
                <div>
                  <Label>Council</Label>
                  <Select value={form.council_id} onValueChange={(v) => setForm({ ...form, council_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select council" /></SelectTrigger>
                    <SelectContent>
                      {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delegate</Label>
                  <Select value={form.delegate_id} onValueChange={(v) => setForm({ ...form, delegate_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select delegate" /></SelectTrigger>
                    <SelectContent>
                      {registrations.map((r) => <SelectItem key={r.delegate_id} value={r.delegate_id}>{r.delegate_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </div>
                <div>
                  <Label>Award</Label>
                  <Select value={form.award_type} onValueChange={(v) => setForm({ ...form, award_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AWARD_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rationale</Label>
                  <Textarea value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} rows={3} />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={openCert} onOpenChange={setOpenCert}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#D4AF37] text-[#0A192F]" data-testid="issue-cert-button">
                <ScrollText className="h-4 w-4 mr-1" /> Issue certificate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Issue certificate</DialogTitle></DialogHeader>
              <form onSubmit={issueCert} className="space-y-4" data-testid="cert-form">
                <div>
                  <Label>Delegate</Label>
                  <Select value={certForm.delegate_id} onValueChange={(v) => setCertForm({ ...certForm, delegate_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select delegate" /></SelectTrigger>
                    <SelectContent>
                      {registrations.map((r) => <SelectItem key={r.delegate_id} value={r.delegate_id}>{r.delegate_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Award type</Label>
                  <Select value={certForm.award_type} onValueChange={(v) => setCertForm({ ...certForm, award_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AWARD_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Council</Label>
                  <Input value={certForm.council} onChange={(e) => setCertForm({ ...certForm, council: e.target.value })} />
                </div>
                <div>
                  <Label>Country (optional, else uses allocation)</Label>
                  <Input value={certForm.country} onChange={(e) => setCertForm({ ...certForm, country: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Issue</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Awards granted</h3>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {awards.map((a) => (
              <div key={a.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`award-card-${a.id}`}>
                <Award className="h-5 w-5 text-[#D4AF37]" />
                <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
                  {a.award_type}
                </p>
                <h4 className="mt-1 font-semibold text-[#0A192F]">{a.delegate_name}</h4>
                <p className="text-xs text-slate-500">{a.country}</p>
                {a.rationale && <p className="text-xs text-slate-600 mt-2 italic">"{a.rationale}"</p>}
              </div>
            ))}
            {awards.length === 0 && (
              <p className="text-sm text-slate-500 col-span-full">No awards yet.</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
